/* eslint-disable @typescript-eslint/no-explicit-any */
import { DropdownOption } from '@src/CommonComponents/DynamicFilters';
import { TIME_PERIOD_OPTIONS, ADJUDICATION_TYPE_OPTIONS } from '../constants/filterOptions';
import { FilterState } from '../types/filters.types';

const SESSION_STATE_KEY = 'state';
const PE_FILTERS_SESSION_KEY = 'providerExperienceFilters';
const GEO_SELECTED_REGION_KEY = 'GEO_SELECTED_REGION';
const GEO_SELECTED_STATES_KEY = 'GEO_SELECTED_STATES';
const GEO_SELECTED_MARKET_KEY = 'GEO_SELECTED_MARKET';

// In-page event used to mirror filter changes committed inside the PEM print preview
// back onto the main-page filter bar, which keeps its own local filter state.
export const PEM_PREVIEW_FILTERS_CHANGED_EVENT = 'pem:print-preview-filters-changed';

export const notifyPreviewFiltersChanged = (filters: FilterState) => {
  try {
    window.dispatchEvent(
      new CustomEvent(PEM_PREVIEW_FILTERS_CHANGED_EVENT, { detail: { filters } })
    );
  } catch {
    // no-op: preview sync must not block filter interaction
  }
};

const isAllValue = (value: unknown): boolean =>
  String(value ?? '')
    .trim()
    .toLowerCase() === 'all';

const asDropdownOption = (option: any): DropdownOption | null => {
  if (!option || typeof option !== 'object') return null;

  const value = option.value ?? option.Value;
  const label = option.label ?? option.Name ?? option.displayName ?? value;
  if (value === undefined || value === null || value === '') return null;

  return {
    ...option,
    id: String(option.id ?? value),
    value: String(value),
    label: String(label),
  } as DropdownOption;
};

const asNonAllDropdownOption = (option: any): DropdownOption | null => {
  const normalized = asDropdownOption(option);
  if (!normalized) return null;
  return isAllValue(normalized.value) ? null : normalized;
};

const asDropdownOptionArray = (options: any): DropdownOption[] => {
  if (!Array.isArray(options)) return [];
  return options
    .map((opt) => asDropdownOption(opt))
    .filter((opt): opt is DropdownOption => Boolean(opt))
    .filter((opt) => !isAllValue(opt.value));
};

export const normalizeRegionValue = (region: unknown): string => {
  const value = String(region ?? '').trim();
  if (!value) return '';
  return value
    .toLowerCase()
    .split(/\s+/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const toGeoSelectionItem = (name: 'REGION' | 'STATES' | 'MARKET', value: string) => ({
  name,
  value,
  disable: false,
  isChipDisable: false,
});

export const parseSessionSelectionArray = (storageKey: string): any[] => {
  try {
    const raw = sessionStorage.getItem(storageKey);
    if (!raw || raw === 'null') return [];
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) return parsed;
    return parsed ? [parsed] : [];
  } catch {
    return [];
  }
};

export const mapSessionSelectionsToOptions = (storageKey: string): DropdownOption[] => {
  const selections = parseSessionSelectionArray(storageKey);

  return selections
    .map((entry) => {
      if (typeof entry === 'string') {
        return asDropdownOption({ id: entry, value: entry, label: entry });
      }

      const value = entry?.value ?? entry?.Value ?? entry?.Name ?? entry?.label;
      const label = entry?.label ?? entry?.Name ?? entry?.value ?? entry?.Value;

      if (value === undefined || value === null || value === '') return null;

      return asDropdownOption({
        ...entry,
        /* istanbul ignore next: label/id fallback chain is exercised via direct helper tests */
        id: String(entry?.id ?? value),
        value: String(value),
        /* istanbul ignore next: label/id fallback chain is exercised via direct helper tests */
        label: String(label ?? value),
      });
    })
    .filter((option): option is DropdownOption => Boolean(option))
    .filter((option) => !isAllValue(option.value));
};

const getSavedProviderExperienceFilters = (): Partial<FilterState> => {
  try {
    const raw = sessionStorage.getItem(PE_FILTERS_SESSION_KEY);
    if (!raw) return {};

    const parsed = JSON.parse(raw);
    return {
      timePeriod: asDropdownOption(parsed.timePeriod),
      lob: asNonAllDropdownOption(parsed.lob),
      taxId: asDropdownOptionArray(parsed.taxId),
      activeTaxId: asDropdownOption(parsed.activeTaxId),
      programs: asNonAllDropdownOption(parsed.programs),
      compareTo: asDropdownOption(parsed.compareTo),
      compareValues: asDropdownOptionArray(parsed.compareValues),
      speciality: asDropdownOptionArray(parsed.speciality),
      adjudicationType: asNonAllDropdownOption(parsed.adjudicationType),
      region: asDropdownOption(parsed.region),
      states: asDropdownOptionArray(parsed.states),
      markets: asDropdownOptionArray(parsed.markets),
    };
  } catch {
    return {};
  }
};

export const removeAllOption = (options: DropdownOption[]): DropdownOption[] =>
  options.filter((option) => String(option?.value || '').toLowerCase() !== 'all');

export const getDefaultTimePeriodOption = (): DropdownOption | null => {
  return TIME_PERIOD_OPTIONS[0] || null;
};

const buildTinOption = (tin: string): DropdownOption => ({
  id: tin,
  value: tin,
  label: tin,
  Name: tin,
  displayName: tin,
  Tin: tin,
  Tinname: tin,
});

const toSharedStateTinOption = (tin: DropdownOption) => ({
  Tin: String(tin.Tin),
  Tinname: String(tin.Tinname),
});

const getTinValuesFromState = (stateTaxId: any): string[] => {
  if (Array.isArray(stateTaxId)) {
    return stateTaxId
      .map((tin) => {
        if (typeof tin === 'string') return tin;
        if (tin && typeof tin === 'object') {
          return String(tin.Tin).trim();
        }
        return '';
      })
      .map((tin) => tin.trim())
      .filter((tin) => Boolean(tin) && !isAllValue(tin));
  }

  if (typeof stateTaxId === 'string') {
    return stateTaxId
      .split(',')
      .map((tin) => tin.trim())
      .filter(Boolean)
      .filter((tin) => tin.toLowerCase() !== 'all');
  }

  if (stateTaxId && typeof stateTaxId === 'object') {
    const value = String(stateTaxId.Tin).trim();
    return value && !isAllValue(value) ? [value] : [];
  }

  return [];
};

const getSessionTaxIds = (): DropdownOption[] => {
  try {
    const stateStr = sessionStorage.getItem(SESSION_STATE_KEY);
    const state = stateStr ? JSON.parse(stateStr) : {};
    const stateTaxIds = getTinValuesFromState(state?.taxId);
    if (stateTaxIds.length > 0) {
      return stateTaxIds.map(buildTinOption);
    }

    const isMultiTinFlow = sessionStorage.getItem('multiTinNavigation') === 'true';
    const isHiVolumeFlow = sessionStorage.getItem('HIVOLUMETINFLOW') === 'true';
    const tins = (sessionStorage.getItem('multiSelectedTins') || '')
      .split(',')
      .map((tin) => tin.trim())
      .filter(Boolean);

    if ((!isMultiTinFlow && !isHiVolumeFlow) || tins.length === 0) {
      return [];
    }

    return tins.map(buildTinOption);
  } catch {
    return [];
  }
};

export const getSessionTimePeriod = (): DropdownOption | null => {
  try {
    const savedFiltersRaw = sessionStorage.getItem(PE_FILTERS_SESSION_KEY);
    if (!savedFiltersRaw) return getDefaultTimePeriodOption();

    const savedFilters = JSON.parse(savedFiltersRaw);
    const savedTimePeriod = savedFilters?.timePeriod;
    const savedValue =
      typeof savedTimePeriod === 'object' ? savedTimePeriod?.value : savedTimePeriod;
    const savedLabel =
      typeof savedTimePeriod === 'object' ? savedTimePeriod?.label : savedTimePeriod;
    const matchedOption = TIME_PERIOD_OPTIONS.find(
      (option) => option.value === savedValue || option.label === savedLabel
    );

    return matchedOption || getDefaultTimePeriodOption();
  } catch {
    return getDefaultTimePeriodOption();
  }
};

export const getSessionRegion = (): DropdownOption | null => {
  try {
    const stateStr = sessionStorage.getItem(SESSION_STATE_KEY);
    /* istanbul ignore next: simple session fallback */
    const stateRegion = stateStr ? JSON.parse(stateStr)?.region : null;
    const geoRegionSelection = mapSessionSelectionsToOptions(GEO_SELECTED_REGION_KEY)[0] || null;
    const rawRegion = stateRegion || geoRegionSelection?.value;
    if (!rawRegion) return null;

    const normalizedRegion = normalizeRegionValue(rawRegion);
    return {
      id: normalizedRegion,
      value: normalizedRegion,
      label: normalizedRegion,
    };
  } catch {
    return null;
  }
};

export const getSessionStates = (): DropdownOption[] => {
  const fromGeoSelections = mapSessionSelectionsToOptions(GEO_SELECTED_STATES_KEY);
  if (fromGeoSelections.length > 0) return fromGeoSelections;

  try {
    const stateStr = sessionStorage.getItem(SESSION_STATE_KEY);
    /* istanbul ignore next: simple session fallback */
    const state = stateStr ? JSON.parse(stateStr) : {};
    return asDropdownOptionArray(state?.states);
  } catch {
    return [];
  }
};

export const getSessionMarkets = (): DropdownOption[] => {
  const fromGeoSelections = mapSessionSelectionsToOptions(GEO_SELECTED_MARKET_KEY);
  if (fromGeoSelections.length > 0) return fromGeoSelections;

  try {
    const stateStr = sessionStorage.getItem(SESSION_STATE_KEY);
    /* istanbul ignore next: simple session fallback */
    const state = stateStr ? JSON.parse(stateStr) : {};
    /* istanbul ignore next: market legacy fallback */
    return asDropdownOptionArray(state?.market || state?.markets);
  } catch {
    return [];
  }
};

const getSessionAdjudicationType = (): DropdownOption | null => {
  try {
    const stateStr = sessionStorage.getItem(SESSION_STATE_KEY);
    if (!stateStr) return null;

    const state = JSON.parse(stateStr);
    const matchedOption = ADJUDICATION_TYPE_OPTIONS.find(
      (option) => option.value === state?.adjudicationType
    );

    return matchedOption || null;
  } catch {
    return null;
  }
};

export const getBaseProviderExperienceFilters = (): FilterState => ({
  timePeriod: getSessionTimePeriod(),
  lob: null,
  taxId: getSessionTaxIds(),
  activeTaxId: null,
  programs: null,
  compareTo: null,
  compareValues: [],
  speciality: [],
  adjudicationType: getSessionAdjudicationType(),
  region: getSessionRegion(),
  states: getSessionStates(),
  markets: getSessionMarkets(),
});

export const getDefaultProviderExperienceFilters = (): FilterState => {
  const baseDefaults = getBaseProviderExperienceFilters();

  const saved = getSavedProviderExperienceFilters();
  const shouldUseSharedTimePeriod = Boolean(baseDefaults.timePeriod?.value);
  const shouldUseSharedTaxIds = baseDefaults.taxId.length > 0;
  const resolvedTimePeriod = shouldUseSharedTimePeriod
    ? baseDefaults.timePeriod
    : saved.timePeriod || baseDefaults.timePeriod;
  let resolvedTaxIds = baseDefaults.taxId;
  if (!shouldUseSharedTaxIds && saved.taxId && saved.taxId.length > 0) {
    resolvedTaxIds = saved.taxId;
  }
  const resolvedStates = baseDefaults.states.length > 0 ? baseDefaults.states : saved.states || [];
  const resolvedMarkets =
    baseDefaults.markets.length > 0 ? baseDefaults.markets : saved.markets || [];
  let resolvedActiveTaxId = resolvedTaxIds.length === 1 ? resolvedTaxIds[0] : null;
  if (!shouldUseSharedTaxIds) {
    resolvedActiveTaxId = saved.activeTaxId || resolvedActiveTaxId;
  }

  return {
    ...baseDefaults,
    ...saved,
    timePeriod: resolvedTimePeriod,
    taxId: resolvedTaxIds,
    activeTaxId: resolvedActiveTaxId,
    speciality: saved.speciality || [],
    states: resolvedStates,
    markets: resolvedMarkets,
    compareValues: saved.compareValues || [],
    region: baseDefaults.region || saved.region || null,
  };
};

export const saveProviderExperienceFiltersToSession = (filters: FilterState) => {
  try {
    const stateStr = sessionStorage.getItem(SESSION_STATE_KEY);
    const currentState = stateStr ? JSON.parse(stateStr) : {};
    const sharedStateTaxIds = filters.taxId.map(toSharedStateTinOption);
    const normalizedRegion = normalizeRegionValue(filters.region?.value || '');
    const normalizedStates = filters.states
      /* istanbul ignore next: persisted state normalization fallback */
      .map((opt) => String(opt?.value || opt?.label || '').trim())
      .filter(Boolean);
    const normalizedMarkets = filters.markets
      /* istanbul ignore next: persisted market normalization fallback */
      .map((opt) => String(opt?.label || opt?.Name || opt?.value || '').trim())
      .filter(Boolean);

    const geoRegionSelections = normalizedRegion
      ? [toGeoSelectionItem('REGION', normalizedRegion)]
      : [];
    const geoStateSelections = normalizedStates.map((value) => toGeoSelectionItem('STATES', value));
    const geoMarketSelections = normalizedMarkets.map((value) =>
      toGeoSelectionItem('MARKET', value)
    );

    const updatedState = {
      ...currentState,
      taxId: sharedStateTaxIds,
      adjudicationType: filters.adjudicationType?.value || '',
      region: normalizedRegion,
      states: normalizedStates,
      market: normalizedMarkets,
    };

    sessionStorage.setItem(GEO_SELECTED_REGION_KEY, JSON.stringify(geoRegionSelections));
    sessionStorage.setItem(GEO_SELECTED_STATES_KEY, JSON.stringify(geoStateSelections));
    sessionStorage.setItem(GEO_SELECTED_MARKET_KEY, JSON.stringify(geoMarketSelections));
    sessionStorage.setItem(SESSION_STATE_KEY, JSON.stringify(updatedState));
    sessionStorage.setItem(PE_FILTERS_SESSION_KEY, JSON.stringify(filters));
  } catch {
    // no-op: session sync should not block page interaction
  }
};

const PROVIDER_INFO_SESSION_KEY = 'providerInfo';

const hasMetricPayloadValue = (value: unknown): boolean => {
  const normalized = String(value ?? '').trim();
  return normalized !== '' && !isAllValue(normalized);
};

const sanitizeMetricPayloadArray = (values: unknown): unknown[] | undefined => {
  if (!Array.isArray(values)) return undefined;
  const cleaned = values.filter((value) => hasMetricPayloadValue(value));
  return cleaned.length > 0 ? cleaned : undefined;
};

const normalizeMetricCompareValues = (
  values: unknown[] | undefined,
  compareBy: string | null
): unknown[] | undefined => {
  if (!values) return undefined;
  if (compareBy !== 'PROVIDER_SYS_KEY' && compareBy !== 'TAX_ID') return values;

  const normalizedValues = values
    .map((value) => {
      const numericValue = typeof value === 'number' ? value : Number(value);
      if (Number.isFinite(numericValue)) return numericValue;
      return compareBy === 'PROVIDER_SYS_KEY' ? String(value).trim() : undefined;
    })
    .filter((value): value is string | number => value !== undefined && value !== '');
  return normalizedValues.length > 0 ? normalizedValues : undefined;
};

const sanitizeMetricPayloadDelimited = (values: unknown): string | undefined => {
  if (Array.isArray(values)) {
    const cleaned = values
      .map((value) => String(value ?? '').trim())
      .filter((value) => hasMetricPayloadValue(value));
    return cleaned.length > 0 ? cleaned.join(',') : undefined;
  }

  if (typeof values === 'string') {
    const cleaned = values
      .split(',')
      .map((value) => value.trim())
      .filter((value) => hasMetricPayloadValue(value));
    return cleaned.length > 0 ? cleaned.join(',') : undefined;
  }

  return undefined;
};

const sanitizeMetricPayloadStringArray = (values: unknown): string[] | undefined => {
  if (Array.isArray(values)) {
    const cleaned = values
      .map((value) => String(value ?? '').trim())
      .filter((value) => hasMetricPayloadValue(value));
    return cleaned.length > 0 ? cleaned : undefined;
  }

  if (typeof values === 'string') {
    const cleaned = values
      .split(',')
      .map((value) => value.trim())
      .filter((value) => hasMetricPayloadValue(value));
    return cleaned.length > 0 ? cleaned : undefined;
  }

  return undefined;
};

/** Current provider's health system, used as the always-locked compare value for Health System compares. */
export const getGlobalHealthSystemOption = (): DropdownOption | null => {
  try {
    const providerInfo = JSON.parse(sessionStorage.getItem(PROVIDER_INFO_SESSION_KEY) || '{}');
    const value = String(providerInfo?.ProviderSysKey ?? '').trim();
    const label = String(providerInfo?.ProviderSystem ?? '').trim();
    if (!value || !label) return null;

    return {
      id: value,
      value,
      label,
      title: label,
      ProviderSysKey: value,
      ProviderSystem: label,
    };
  } catch {
    return null;
  }
};

const lockOption = (option: DropdownOption | null | undefined): DropdownOption[] =>
  option && !isAllValue(option.value) ? [{ ...option, isDisabled: true }] : [];

const lockOptions = (options: DropdownOption[] | undefined): DropdownOption[] =>
  (options || [])
    .filter((option) => option && !isAllValue(option.value))
    .map((option) => ({ ...option, isDisabled: true }));

/**
 * The quick-filter (or global provider) value that must always stay selected and
 * read-only inside the Compare Values dropdown for the current Compare By selection.
 */
export const getLockedCompareOptions = (
  compareTo: string | null | undefined,
  filters: FilterState
): DropdownOption[] => {
  switch (compareTo) {
    case 'LOB':
      return lockOption(filters.lob);
    case 'Tax ID':
      return lockOptions(filters.taxId);
    case 'Speciality':
      return lockOptions(filters.speciality);
    case 'Region':
      return lockOption(filters.region);
    case 'States':
      return lockOptions(filters.states);
    case 'Markets':
      return lockOptions(filters.markets);
    case 'Programs':
      return lockOption(filters.programs);
    case 'Adjudication Type':
      return lockOption(filters.adjudicationType);
    case 'Time Period':
      return lockOption(filters.timePeriod);
    case 'Health System':
      return lockOption(getGlobalHealthSystemOption());
    default:
      return [];
  }
};

/** Locked options merged with any additional user-selected compare values, locked ones taking precedence. */
export const getEffectiveCompareValues = (filters: FilterState): DropdownOption[] => {
  const locked = getLockedCompareOptions(filters.compareTo?.value, filters);
  const lockedKeys = new Set(locked.map((option) => String(option.value)));
  const extras = (filters.compareValues || []).filter(
    (option) => !option.isDisabled && !lockedKeys.has(String(option.value))
  );
  return [...locked, ...extras];
};

const getTaxIdValue = (value: unknown): unknown => {
  if (typeof value !== 'object' || value === null) return value;
  const selection = value as { value?: unknown; Tin?: unknown };
  return selection.value ?? selection.Tin;
};

export const buildProviderExperiencePayload = (
  basePayload: any,
  filters: FilterState,
  isGeoFlow: boolean
) => {
  const normalizeAllToEmpty = (option: DropdownOption | null): string => {
    if (!option?.value) return '';
    const value = String(option.value);
    return isAllValue(value) ? '' : value;
  };

  // Compare API responses key each series by raw value (e.g. "EI"); this lets card UIs
  // show the selected option's display name instead of the raw compare value.
  const compareValueLabels = Object.setPrototypeOf(
    Object.fromEntries(
      getEffectiveCompareValues(filters).map((option) => [
        String(option.value),
        String(option.label ?? option.title ?? option.value),
      ])
    ),
    null
  ) as Record<string, string>;

  return {
    ...basePayload,
    lineOfBusiness: filters.lob?.value || 'ALL',
    timePeriod: filters.timePeriod?.value || '',
    taxId:
      filters.activeTaxId?.value ||
      (filters.taxId.length > 0 ? filters.taxId.map((tin) => tin.value).join(',') : 'All'),
    programs: normalizeAllToEmpty(filters.programs),
    compareTo: filters.compareTo?.value || null,
    compareValues: getEffectiveCompareValues(filters).map((opt) => opt.value),
    compareValueLabels,
    speciality:
      filters.speciality && filters.speciality.length > 0
        ? filters.speciality.map((opt) => opt.value).join(',')
        : 'All',
    adjudicationType: normalizeAllToEmpty(filters.adjudicationType),
    region: normalizeRegionValue(filters.region?.value || 'National') || 'National',
    states:
      filters.states
        ?.map((opt) => String(opt?.value || '').trim())
        .filter(Boolean)
        .join(',') || '',
    markets:
      filters.markets
        ?.map((opt) => String(opt?.label || opt?.Name || opt?.value || '').trim())
        .filter(Boolean) || [],
    flowType: isGeoFlow ? 'GEO' : 'HCO_TIN',
  };
};

/**
 * Single source of truth for building a PemDetails metric-card API payload from the
 * applied Provider Experience filter payload (the one stored in ProviderExperienceReducer).
 *
 * Every metric card (Effort Index, Contract Yield, Average Claim Processing, ...) can spread
 * the result and append its own card-specific params. Only meaningfully-selected filters are
 * included; defaults ("All"/empty) are omitted so the payload stays clean and stable.
 */
export const buildPemMetricPayload = (
  filterPayload: Record<string, any> = {},
  options: { preserveTimePeriod?: boolean } = {}
): Record<string, any> => {
  const source = filterPayload || {};
  const params: Record<string, any> = {};
  const compareByMap: Record<string, string> = {
    Region: 'REGIONS',
    States: 'STATES',
    Markets: 'MARKETS',
    LOB: 'LOB',
    Speciality: 'SPECIALTY',
    Programs: 'PROGRAMS',
    'Adjudication Type': 'ADJUDICATION_TYPE',
    'Health System': 'PROVIDER_SYS_KEY',
    'Time Period': 'TIME_PERIOD',
    'Tax ID': 'TAX_ID',
  };

  // CompareBy is only meaningful once the user has actually picked compare values.
  const sanitizedCompareValues = sanitizeMetricPayloadArray(source.compareValues);
  const compareBy = sanitizedCompareValues
    ? compareByMap[String(source.compareTo || '').trim()] || null
    : null;
  const compareValues = normalizeMetricCompareValues(sanitizedCompareValues, compareBy);

  function formatDate(date) {
    return new Intl.DateTimeFormat('en-ZA', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(date);
  }

  // Omit any filter whose selected value is All/ALL (or empty) so it is not sent to the API.
  if (hasMetricPayloadValue(source.lineOfBusiness) && compareBy !== 'LOB') {
    params.Lob = source.lineOfBusiness;
  }
  if (
    hasMetricPayloadValue(source.timePeriod) &&
    (options.preserveTimePeriod || compareBy !== 'TIME_PERIOD')
  ) {
    const timeFrames = source.timePeriod.split(' ');
    const timeFrame1: any = formatDate(new Date(timeFrames[0]));
    const timeFrame2: any = formatDate(new Date(timeFrames[2]));
    params.PeriodStart = timeFrame1.replace(/\//g, '-');
    params.PeriodEnd = timeFrame2.replace(/\//g, '-');
  }
  if (source.flowType !== 'GEO' && compareBy !== 'PROVIDER_SYS_KEY') {
    const providerSysKey =
      JSON.parse(sessionStorage.getItem('providerInfo') ?? '{"ProviderSysKey": ""}')
        ?.ProviderSysKey ?? '';
    if (providerSysKey) params.ProviderSysKey = providerSysKey;
  }
  const taxIdValue = source.activeTaxId ?? source.taxId;
  const taxId = Array.isArray(taxIdValue)
    ? taxIdValue
      .map(getTaxIdValue)
      .map((value) => String(value ?? '').trim())
      .filter((value) => hasMetricPayloadValue(value))
      .join(',')
    : String(getTaxIdValue(taxIdValue) ?? '').trim();
  if (hasMetricPayloadValue(taxId) && compareBy !== 'TAX_ID') params.Tin = taxId;
  if (hasMetricPayloadValue(source.programs) && compareBy !== 'PROGRAMS') {
    params.Program = source.programs;
  }
  if (hasMetricPayloadValue(source.speciality) && compareBy !== 'SPECIALTY') {
    params.Speciality = source.speciality;
  }
  if (hasMetricPayloadValue(source.adjudicationType) && compareBy !== 'ADJUDICATION_TYPE') {
    params.AdjudicationType = source.adjudicationType;
  }
  if (compareBy) params.CompareBy = compareBy;
  if (compareValues) params.CompareValues = compareValues;

  if (
    source.flowType === 'GEO' &&
    compareBy !== 'REGIONS' &&
    hasMetricPayloadValue(source.region)
  ) {
    params.Region = normalizeRegionValue(source.region);
  }

  const states = sanitizeMetricPayloadDelimited(source.states);
  if (states && compareBy !== 'STATES') params.State = states;

  const markets = sanitizeMetricPayloadStringArray(source.markets);
  if (markets && compareBy !== 'MARKETS') params.Market = markets;

  return params;
};
